import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useWorkEnvironmentMeasuresQuery } from '../../hooks/queries/useWorkEnvironmentMeasuresQuery';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { ProcessHero } from './components/ProcessHero';
import { ShareCard } from './components/ShareCard';
import { DiscussionsCard } from './measures/DiscussionsCard';
import { HighlightProgressCard } from './measures/HighlightProgressCard';
import { MeasureAreasCard } from './measures/MeasureAreasCard';
import { MeasuresNextCard } from './measures/MeasuresNextCard';
import { ProposalDialog } from './measures/ProposalDialog';
import { ProposalsCard } from './measures/ProposalsCard';
import { measuresTag, ranksByHighlights, shareKindOf, teamLink } from './measuresView';
import { currentStepIndex } from './surveyProcessView';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';

/** Step 5 and 6: the team proposes measures, then points out the ones that matter most */
export const WorkEnvironmentSurveyMeasures = ({ survey }: { survey: WorkEnvironmentSurvey }): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useWorkEnvironmentMeasuresQuery(survey.id, questionLanguageFor(i18n.language));
  const [proposingIn, setProposingIn] = useState<string | null>(null);
  const team = survey.teamName;
  const shareKind = data ? shareKindOf(data.step) : null;

  return (
    <div data-size="md">
      <ProcessHero
        title={survey.title}
        intro={t(`workEnvironmentMeasures.intro.${data?.step ?? 'propose'}`, { team })}
        tag={{
          label: data ? measuresTag(data.step, data.deadline, t) : t('workEnvironmentMeasures.tag.plan'),
          color: 'info',
        }}
        process={{ current: currentStepIndex(survey.status), createdAt: survey.createdAt, responseDeadline: null }}
      />
      <div className={process.layout}>
        <div className={classes.mainColumn}>
          {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
          {isError && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentMeasures.error')}</Paragraph>
            </Alert>
          )}
          {data?.step === 'propose' && <MeasureAreasCard areas={data.areas} onPropose={setProposingIn} />}
          {data && ranksByHighlights(data.step) && (
            <HighlightProgressCard voters={data.voters} expected={data.expected} max={data.maxHighlights} />
          )}
          {data && (
            <ProposalsCard
              surveyId={survey.id}
              step={data.step}
              areas={data.areas}
              proposals={data.proposals}
              onAdd={() => setProposingIn(data.areas[0]?.key ?? null)}
            />
          )}
        </div>
        {data && (
          <div className={process.side}>
            {shareKind && (
              <ShareCard
                link={teamLink(window.location.origin, survey.uuid)}
                text={t(`workEnvironmentMeasures.share.${data.step}`)}
                linkLabel={t(`workEnvironmentMeasures.share.${data.step}Link`)}
                sharing={{ surveyId: survey.id, teamId: survey.teamId, kind: shareKind }}
              />
            )}
            <MeasuresNextCard surveyId={survey.id} step={data.step} deadline={data.deadline} />
            <DiscussionsCard surveyId={survey.id} />
          </div>
        )}
      </div>
      {data && proposingIn && (
        <ProposalDialog
          surveyId={survey.id}
          areas={data.areas}
          areaKey={proposingIn}
          onClose={() => setProposingIn(null)}
        />
      )}
    </div>
  );
};
